import api from './api.service';
import type { RejectionPayload } from './rejection.service';

export interface SalesOrderItem {
  id?: string;
  sales_order_id?: string;
  line_number: number;
  item_code?: string;
  item_description: string;
  revision?: string | null;
  item_category?: string;
  ordered_quantity: number;
  delivered_quantity?: number;
  invoiced_quantity?: number;
  uom?: string;
  unit_price: number;
  line_total: number;
  discount_percentage?: number;
  discount_amount?: number;
  net_amount: number;
  vat_applicable?: boolean;
  vat_percentage?: number;
  vat_amount?: number;
  required_date?: string;
  delivery_days?: number;
  project_id?: string;
  cost_center_id?: string;
  is_fully_delivered?: boolean;
  is_fully_invoiced?: boolean;
  notes?: string;
  specifications?: string;
  created_at?: string;
  updated_at?: string;
}

export interface SalesOrder {
  id: string;
  company_id: string;
  sales_order_number: string;
  quotation_id?: string;
  customer_po_number?: string;
  customer_po_date?: string;
  contract_review_id?: string | null;
  opportunity_id?: string;
  estimation_id?: string | null;
  boq_id?: string | null;
  trading_rental_estimation_id?: string | null;
  /** delivery | timesheet */
  fulfillment_mode?: 'delivery' | 'timesheet' | string;
  source_estimation_type?: string | null;
  revenue_service_type_id?: string | null;

  // Customer information
  customer_id: string;
  customer_name: string;
  customer_address?: string;
  customer_contact?: string;
  customer_email?: string;
  customer_phone?: string;
  
  // Order details
  order_date: string;
  required_date?: string;
  promised_date?: string;
  expected_delivery_date?: string;
  order_type?: string;
  
  // Project linking
  project_id?: string;
  project_name?: string;
  is_project_linked?: boolean;
  department_id?: string | null;

  shipping_address?: string;
  billing_address?: string;
  notes?: string;

  // Financial
  subtotal: number;
  discount_percentage?: number;
  discount_amount?: number;
  total_after_discount: number;
  vat_applicable?: boolean;
  vat_percentage?: number;
  vat_amount?: number;
  grand_total: number;
  currency?: string;
  
  // Payment
  payment_terms?: string;
  advance_payment_percentage?: number;
  advance_payment_amount?: number;
  advance_reversed_amount?: number;
  retention_percentage?: number;
  retention_amount?: number;
  retention_withheld_amount?: number;
  is_milestone_based?: boolean;

  // Delivery
  delivery_terms?: string;
  delivery_address?: string;
  shipping_method?: string;
  
  // Status
  order_status: string;
  approval_instance_id?: string | null;
  approval_status?: string;
  /** NM_WP_66: pending_review until sales marks reviewed */
  sales_review_status?: string;
  sales_reviewed_by?: string | null;
  sales_reviewed_at?: string | null;
  sales_review_remarks?: string | null;
  approved_by?: string;
  approved_date?: string;
  
  // Fulfillment
  is_fully_delivered?: boolean;
  is_fully_invoiced?: boolean;
  
  // Assignment
  sales_person?: string;
  sales_team_id?: string | null;
  sales_team_name?: string | null;
  /** User expected to take the next step; feeds unified tasks assignee on sync. */
  next_action_user_id?: string | null;

  // Notes
  customer_notes?: string;
  internal_notes?: string;
  terms_and_conditions?: string;
  special_instructions?: string;
  commercial_scope_text?: string | null;
  quality_assurance_text?: string | null;
  customer_requirements_text?: string | null;
  
  // Documents
  pdf_file_url?: string;
  
  // Audit
  created_by?: string;
  created_at: string;
  updated_at: string;
  is_deleted?: boolean;
  
  // Relations
  items?: SalesOrderItem[];
  milestones?: SalesOrderMilestone[];
}

/** NM_WP_68–69: history when SO is edited after work orders exist */
export interface SalesOrderAmendment {
  id: string;
  company_id: string;
  sales_order_id: string;
  amended_at: string;
  amended_by?: string | null;
  reason: string;
  workload_impact_note?: string | null;
  snapshot_json?: Record<string, unknown> | null;
}

export interface SalesOrderMilestone {
  id?: string;
  sales_order_id?: string;
  milestone_number: number;
  milestone_name: string;
  description?: string;
  deliverables?: string;
  planned_date?: string;
  actual_date?: string;
  milestone_value?: number;
  billing_percentage?: number;
  milestone_status?: string;
  completion_percentage?: number;
  is_invoiced?: boolean;
  invoice_id?: string;
  notes?: string;
}

export interface SalesOrderRequest {
  order: Partial<SalesOrder>;
  items: SalesOrderItem[];
  milestones?: SalesOrderMilestone[];
}

export interface SalesOrderCostingReport {
  header: {
    sales_order_id: string;
    sales_order_number: string;
    order_date: string | null;
    order_status: string | null;
    customer_name: string | null;
    currency: string;
    project_id: string | null;
    project_number: string | null;
    project_name: string | null;
    quotation_id: string | null;
    quotation_number: string | null;
    quotation_revision: number | null;
    attribution_method: 'revenue_share' | 'sole_so' | null;
    attribution_share: number | null;
  };
  sell: {
    quotation_value: number | null;
    order_value_excl_vat: number;
    vat_amount: number;
    order_value_incl_vat: number;
    invoiced: number;
    collected: number;
    outstanding: number;
    advance_adjusted: number;
    uninvoiced: number;
    invoices: Array<{
      id: string;
      invoice_number: string;
      invoice_date: string | null;
      invoice_type: string;
      invoice_status: string;
      payment_status: string;
      billed: number;
      collected: number;
      outstanding: number;
      is_shared: boolean;
    }>;
  };
  estimate: {
    source_type: string | null;
    source_label: string | null;
    link_path: string | null;
    cost_total: number;
    selling_total: number | null;
    hours_total: number | null;
    margin: number;
    margin_pct: number | null;
    breakdown: Array<{ key: string; label: string; amount: number }>;
    lines: Array<{
      id: string;
      line_number: number;
      description: string;
      line_type: string | null;
      quantity: number;
      estimate_cost: number;
      estimate_selling: number | null;
      margin: number | null;
      margin_pct: number | null;
      estimate_hours: number | null;
      actual_hours: number | null;
    }>;
  };
  actual: {
    basis: string;
    cost_total: number;
    project_cost_total: number;
    project_posted_cost_total: number | null;
    hours_total: number | null;
    by_category: Array<{ category: string; project_total: number; attributed: number }>;
    transactions: Array<{
      id: string;
      cost_date: string | null;
      cost_category: string | null;
      source_type: string | null;
      source_document_number: string | null;
      description: string | null;
      quantity: number;
      unit_cost: number;
      total_cost: number;
    }>;
    transactions_truncated: boolean;
    goods_received: Array<{
      grn_id: string;
      grn_number: string | null;
      grn_date: string | null;
      grn_status: string | null;
      supplier_name: string | null;
      po_number: string | null;
      item_code: string | null;
      item_description: string | null;
      quantity: number;
      unit_price: number;
      value: number;
    }>;
    procurement: {
      po_ordered: number;
      grn_received: number;
      supplier_invoiced: number;
      supplier_paid: number;
    } | null;
  };
  committed: {
    open_po_value_project: number;
    open_po_value_attributed: number;
    lines: Array<{
      po_id: string;
      po_number: string | null;
      po_date: string | null;
      po_status: string | null;
      supplier_name: string | null;
      line_number: number;
      item_code: string | null;
      item_description: string | null;
      uom: string | null;
      ordered_quantity: number;
      received_quantity: number;
      open_quantity: number;
      unit_price: number;
      open_value: number;
    }>;
  };
  margin: {
    revenue: number;
    estimated_cost: number;
    estimated_margin: number;
    estimated_margin_pct: number | null;
    actual_cost: number;
    actual_margin: number;
    actual_margin_pct: number | null;
    open_committed: number;
    projected_cost: number;
    projected_margin: number;
    projected_margin_pct: number | null;
    cost_variance: number;
    cost_variance_pct: number | null;
  };
}

export interface SalesOrderFilters {
  order_status?: string;
  approval_status?: string;
  customer_id?: string;
  project_id?: string;
  opportunity_id?: string;
  date_from?: string;
  date_to?: string;
  search?: string;
  sales_team_id?: string;
  pending_work_order?: string | boolean;
  fulfillment_mode?: 'delivery' | 'timesheet' | string;
}

class SalesOrderService {
  /**
   * Get all sales orders with optional filters
   */
  async getAll(filters?: SalesOrderFilters): Promise<SalesOrder[]> {
    try {
      const params = new URLSearchParams();
      if (filters) {
        Object.entries(filters).forEach(([key, value]) => {
          if (value) params.append(key, value);
        });
      }
      const response = await api.get(`/sales/orders?${params.toString()}`);
      const res = response as { data?: SalesOrder[] | { data?: SalesOrder[] }; success?: boolean };
      // Handle different response structures: { data: [] }, { data: { data: [] } }, or raw array
      if (Array.isArray(res.data)) {
        return res.data;
      }
      if (res.data && typeof res.data === 'object' && Array.isArray((res.data as any).data)) {
        return (res.data as any).data;
      }
      if (Array.isArray(res)) {
        return res as unknown as SalesOrder[];
      }
      console.warn('Unexpected response structure:', res);
      return [];
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to fetch customer orders';
      console.error('Error fetching sales orders:', {
        message: errorMessage,
        status: error.response?.status,
        data: error.response?.data,
        fullError: error
      });
      
      // Show error message to user if it's a 400 error (likely company_id issue)
      if (error.response?.status === 400) {
        console.error('⚠️ Backend error (400):', errorMessage);
        // You might want to show a notification here
        // message.error(errorMessage);
      }
      
      return [];
    }
  }

  /**
   * Get a single sales order by ID
   */
  async getById(id: string): Promise<SalesOrder> {
    const response = await api.get(`/sales/orders/${id}`);
    console.log('Sales Order Detail API Response:', response.data);

    // Handle different response structures
    if (response.data?.data) {
      return response.data.data;
    }
    return response.data;
  }

  /**
   * Line items for a sales order (includes DB `id` per row — required for WO conversion).
   */
  async getOrderItems(orderId: string): Promise<SalesOrderItem[]> {
    const response = await api.get<SalesOrderItem[]>(`/sales/orders/${orderId}/items`);
    const payload = response.data as SalesOrderItem[] | { data?: SalesOrderItem[] } | undefined;
    if (Array.isArray(payload)) {
      return payload;
    }
    if (payload && typeof payload === 'object' && Array.isArray(payload.data)) {
      return payload.data;
    }
    return [];
  }

  /** Amendments when SO is updated while linked work orders exist (re-review path). */
  async listAmendments(salesOrderId: string): Promise<SalesOrderAmendment[]> {
    const response = await api.get(`/sales/orders/${salesOrderId}/amendments`);
    const rows = (response as { data?: SalesOrderAmendment[] }).data;
    return Array.isArray(rows) ? rows : [];
  }

  /**
   * Create a new sales order
   */
  async create(data: SalesOrderRequest): Promise<SalesOrder> {
    const response = await api.post('/sales/orders', data);
    return response.data.data;
  }

  /**
   * Update an existing sales order
   */
  async update(id: string, data: SalesOrderRequest): Promise<SalesOrder> {
    const response = await api.put(`/sales/orders/${id}`, data);
    return response.data.data;
  }

  /** Partial header update (body is flattened order fields; omits items). */
  async updateHeader(id: string, patch: Partial<SalesOrder>): Promise<SalesOrder> {
    const response = await api.put(`/sales/orders/${id}`, patch);
    const payload = response.data?.data ?? response.data;
    return payload as SalesOrder;
  }

  /**
   * Delete a sales order
   */
  async delete(id: string): Promise<void> {
    await api.delete(`/sales/orders/${id}`);
  }

  /**
   * Update sales order status
   */
  /** Billing (customer_address) / shipping (delivery_address) only — allowed for pending approvers. */
  async updateAddresses(
    id: string,
    body: { billing_address?: string; shipping_address?: string }
  ): Promise<SalesOrder> {
    const response = await api.patch(`/sales/orders/${id}/addresses`, body);
    return response.data?.data ?? response.data;
  }

  async updateStatus(id: string, status: string): Promise<SalesOrder> {
    const response = await api.patch(`/sales/orders/${id}/status`, { status });
    return response.data.data;
  }

  /**
   * Submit draft order for universal approval (no-op if instance exists; backend requires flag on).
   */
  async submitForApproval(id: string): Promise<SalesOrder> {
    const response = await api.post(`/sales/orders/${id}/submit-for-approval`, {});
    const raw = (response as any)?.data ?? response;
    return raw?.data ?? raw;
  }

  /** NM_WP_66: sales team records review; notifies approvers. */
  async recordSalesReview(id: string, remarks?: string): Promise<SalesOrder> {
    const response = await api.post(`/sales/orders/${id}/sales-review`, { remarks: remarks ?? undefined });
    const raw = (response as any)?.data ?? response;
    return raw?.data ?? raw;
  }

  /**
   * Approve a sales order
   */
  async approve(id: string, remarks?: string): Promise<SalesOrder> {
    const response = await api.post(`/sales/orders/${id}/approve`, { remarks: remarks ?? undefined });
    const raw = (response as any)?.data ?? response;
    return raw?.data ?? raw;
  }

  async rejectApproval(id: string, payload: RejectionPayload): Promise<SalesOrder> {
    const response = await api.post(`/sales/orders/${id}/reject-approval`, payload);
    const raw = (response as any)?.data ?? response;
    return raw?.data ?? raw;
  }

  async revokeApproval(id: string, reason: string): Promise<SalesOrder> {
    const response = await api.post(`/sales/orders/${id}/revoke-approval`, { reason });
    const raw = (response as any)?.data ?? response;
    return raw?.data ?? raw;
  }

  /**
   * Cancel a sales order
   */
  async cancel(id: string): Promise<SalesOrder> {
    const response = await api.post(`/sales/orders/${id}/cancel`);
    return response.data.data;
  }

  async closeTimesheetFulfillment(id: string): Promise<SalesOrder> {
    const response = await api.post(`/sales/orders/${id}/close-timesheet-fulfillment`);
    return response.data.data;
  }

  /**
   * Get advance invoice eligibility for a sales order
   */
  async getAdvanceEligibility(salesOrderId: string): Promise<AdvanceEligibilityResponse> {
    const response = await api.get(`/sales/orders/${salesOrderId}/advance-eligibility`);
    const raw = (response as any)?.data ?? response;
    return raw?.data ?? raw;
  }

  /**
   * Aggregated order health / 360° status check (deliveries, invoices, WOs, advance snapshot).
   */
  async getOrderStatusCheck(orderId: string): Promise<OrderStatusCheckResponse> {
    const response = await api.get(`/sales/orders/${orderId}/status-check`);
    const raw = (response as any)?.data ?? response;
    return raw?.data ?? raw;
  }

  /** Single-order project costing: sell side, estimate, actual, open commitments, margin. */
  async getProjectCostingReport(orderId: string): Promise<SalesOrderCostingReport> {
    const response = await api.get(`/sales/reports/project-costing/${orderId}`);
    const raw = (response as any)?.data ?? response;
    return raw?.data ?? raw;
  }

  /**
   * Create an advance invoice from a sales order
   */
  async createAdvanceInvoice(
    salesOrderId: string,
    payload: CreateAdvanceInvoicePayload
  ): Promise<import('./salesInvoice.service').SalesInvoice> {
    const response = await api.post(`/sales/orders/${salesOrderId}/advance-invoice`, payload);
    const raw = (response as any)?.data ?? response;
    return raw?.data ?? raw;
  }
}

export interface AdvanceEligibilityResponse {
  eligible: boolean;
  advancePaymentAmount: number;
  advanceInvoicedAmount: number;
  remainingAdvance: number;
  sales_order?: SalesOrder | null;
}

export interface OrderStatusCheckDeliverySummary {
  id: string;
  delivery_note_number: string;
  delivery_date: string;
  delivery_status: string;
  sales_order_id: string;
}

export interface OrderStatusCheckInvoiceSummary {
  id: string;
  invoice_number: string;
  invoice_date: string;
  due_date?: string;
  invoice_status: string;
  payment_status: string;
  invoice_type: string;
  grand_total: number;
  balance_due: number;
  /** This sales order's share of billed (Advance = 0). */
  attributed_billed?: number;
  /** This sales order's share of grand_total − balance_due. */
  attributed_collected?: number;
  /** This sales order's share of balance_due. */
  attributed_ar?: number;
  attributed_advance_adjusted?: number;
  is_shared?: boolean;
  /** Customer advance applied to this invoice (excluded from “Collected” cash-like total on order health). */
  advance_adjusted_amount?: number;
  sales_order_id?: string;
}

export interface OrderStatusCheckQuotationSource {
  quotation_id: string;
  quotation_number: string;
  estimation_id: string | null;
  estimation_number?: string | null;
  boq_id: string | null;
  boq_number: string | null;
  trading_rental_estimation_id?: string | null;
  trading_rental_estimation_number?: string | null;
}

export interface OrderStatusCheckOpportunitySource {
  id: string;
  opportunity_number: string;
  opportunity_name: string | null;
  status: string | null;
  link_path: string;
}

export interface OrderStatusCheckOrderCosting {
  order_revenue: number;
  estimation_cost: number;
  gross_profit: number;
  margin_pct_on_revenue: number;
  source_type: 'trading_rental_estimation' | 'boq' | 'multi_item_estimation' | null;
  source_id: string | null;
  source_label: string | null;
  link_path: string | null;
}

export interface OrderStatusCheckLinkedCreditNote {
  id: string;
  credit_note_number: string;
  credit_note_date: string;
  total_amount: number;
  status: string;
  original_sales_invoice_id: string;
}

export interface OrderStatusCheckWorkOrderSummary {
  id: string;
  work_order_number: string;
  work_order_status: string;
  order_date?: string;
  planned_quantity?: number;
  produced_quantity?: number;
}

export interface OrderStatusCheckReceiptAllocation {
  allocation_id: string;
  receipt_voucher_id: string;
  receipt_number: string;
  receipt_date: string;
  sales_invoice_id: string;
  invoice_number: string;
  allocated_amount: number;
}

export interface OrderStatusCheckCreditNoteAllocation {
  allocation_id: string;
  customer_credit_note_id: string;
  credit_note_number: string;
  credit_note_date: string;
  sales_invoice_id: string;
  invoice_number: string;
  allocated_amount: number;
}

export interface OrderStatusCheckResponse {
  sales_order: SalesOrder;
  deliveries: OrderStatusCheckDeliverySummary[];
  invoices: OrderStatusCheckInvoiceSummary[];
  advance_eligibility: {
    eligible: boolean;
    advancePaymentAmount: number;
    advanceInvoicedAmount: number;
    remainingAdvance: number;
  };
  work_orders: OrderStatusCheckWorkOrderSummary[];
  receipt_allocations?: OrderStatusCheckReceiptAllocation[];
  credit_note_allocations?: OrderStatusCheckCreditNoteAllocation[];
  quotation_source?: OrderStatusCheckQuotationSource | null;
  opportunity_source?: OrderStatusCheckOpportunitySource | null;
  linked_credit_notes?: OrderStatusCheckLinkedCreditNote[];
  order_costing?: OrderStatusCheckOrderCosting;
  performance?: import('../types/estimationPerformance').EstimationPerformanceDto;
}

export interface CreateAdvanceInvoicePayload {
  amount: number;
  apply_tax: boolean;
  vat_percentage?: number;
  vat_account_id?: string;
  prepaid_customer_advance_account_id: string;
  advance_from_customer_account_id: string;
  department_id?: string;
}

const salesOrderService = new SalesOrderService();
export default salesOrderService;
